import { GridColDef, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid-pro'
import { gridFormatters, gridValueGetters } from 'src/utils/grid'
import { Contractor, ContractorWithDeactivationDetails } from 'src/interfaces/contractor'
import { ContractorStatusChip } from 'src/components/contractor-list/shared/ContractorStatusChip'
import { TableTags } from 'src/components/tags/TableTags'
import { ContractorState, ContractorStatus } from './enums'
import { Tag } from 'src/interfaces/tag'
import { contractorStatusTitles, contractorTypeTitles } from './constants'
import { ContractorTypeChip } from 'src/components/contractor-list/shared/ContractorTypeChip'

const sharedContractorColumns: Array<GridColDef> = [
  {
    field: 'externalId',
    headerName: 'Contractors ID',
    flex: 2,
    sortable: true,
  },
  {
    field: 'name',
    headerName: 'Name',
    flex: 2,
    sortable: true,
  },
  {
    field: 'contractorType',
    headerName: 'Type',
    flex: 2,
    sortable: true,
    valueGetter: (params: GridValueGetterParams<Contractor>) =>
      contractorTypeTitles[params.row.contractorType],
    renderCell: (params: GridRenderCellParams<Contractor>) => {
      return <ContractorTypeChip type={params.row.contractorType} />
    },
  },
]

const statusCol = {
  field: 'status',
  headerName: 'Status',
  flex: 2,
  sortable: true,
  valueGetter: (params: GridValueGetterParams<Contractor>) =>
    contractorStatusTitles[params.row.status],
  renderCell: (params: GridRenderCellParams<Contractor>) => {
    return <ContractorStatusChip status={params.row.status} />
  },
}

const deactivationDetailsCol: GridColDef = {
  field: 'deactivationDetails',
  headerName: 'Deactivation details',
  flex: 3,
  sortable: false,
  renderCell: (params: GridRenderCellParams<ContractorWithDeactivationDetails>) => {
    const { deactivationReason, deactivatedByEmail, dateDeactivated, deactivationInfo } = params.row

    return (
      <ContractorStatusChip
        status={ContractorStatus.Deactivated}
        deactivationReason={deactivationReason}
        deactivatedByEmail={deactivatedByEmail}
        deactivatedAt={dateDeactivated}
        expiredSteps={[deactivationInfo?.stepType]}
      />
    )
  },
}

const lastColumns = [
  {
    field: 'phone',
    headerName: 'Phone number',
    flex: 2,
    sortable: true,
  },
  {
    field: 'email',
    headerName: 'Email',
    flex: 3,
    sortable: true,
  },
  {
    field: 'tags',
    headerName: 'Tag(s)',
    flex: 3,
    sortable: false,
    sortComparator: (v1: Tag[], v2: Tag[]) => {
      return v1.length - v2.length
    },
    renderCell: (params: GridRenderCellParams<Contractor>) =>
      params.row?.tags.length ? (
        <TableTags tags={params.row?.tags || []} cutLength={5} tooltipThreshold={5} />
      ) : (
        <span>-</span>
      ),
  },
  {
    field: 'referral',
    headerName: 'Referral',
    flex: 2,
    sortable: true,
    valueGetter: (params: GridValueGetterParams<Contractor>) => params.value || '-',
  },
]

const mcCol = [
  {
    field: 'masterContractorName',
    headerName: 'Master contractor name',
    flex: 3,
    sortable: true,
    valueGetter: (params: GridValueGetterParams<Contractor>) => params.value || '-',
  },
]

export const contractorColumns = {
  [ContractorState.Active]: [
    ...sharedContractorColumns,
    statusCol,
    ...lastColumns,
    {
      field: 'dateOnboarded',
      headerName: 'Date onboarded',
      flex: 2,
      sortable: true,
      valueGetter: gridValueGetters.parseDate,
      valueFormatter: gridFormatters.formatShortDate,
    },
    ...mcCol,
  ],
  [ContractorState.Deactivated]: [
    ...sharedContractorColumns,
    deactivationDetailsCol,
    ...lastColumns,
    {
      field: 'dateDeactivated',
      headerName: 'Date deactivated',
      flex: 2,
      sortable: true,
      valueGetter: gridValueGetters.parseDate,
      valueFormatter: gridFormatters.formatShortDate,
    },
    ...mcCol,
  ],
  [ContractorState.Recollection]: [...sharedContractorColumns, statusCol, ...lastColumns, ...mcCol],
}

export const contractorInitialSortFields = {
  [ContractorState.Active]: 'dateOnboarded',
  [ContractorState.Deactivated]: 'dateDeactivated',
  [ContractorState.Recollection]: undefined,
}

export const contractorPageSizeOptions = [100, 250, 500, 1000]
